// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';

/**
 * Reusable Loading State Component
 * Provides consistent loading UI across the application
 */

export const LoadingSpinner = ({ size = 'md', color = 'blue' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colorClasses = {
    blue: 'border-blue-600',
    green: 'border-green-600',
    purple: 'border-purple-600',
    orange: 'border-orange-600',
    gray: 'border-gray-600'
  };

  return (
    <div className={`${sizeClasses[size]} border-4 ${colorClasses[color]} border-t-transparent rounded-full animate-spin`} />
  );
};

export const LoadingOverlay = ({ message = 'Loading...', size = 'lg' }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center space-y-4">
        <LoadingSpinner size={size} />
        <p className="text-gray-700 font-medium">{message}</p>
      </div>
    </div>
  );
};

export const LoadingCard = ({ message = 'Loading...', icon = '⏳' }) => {
  return (
    <div className="bg-white rounded-lg shadow p-8 flex flex-col items-center justify-center space-y-4">
      <div className="text-5xl animate-pulse">{icon}</div>
      <p className="text-gray-600 font-medium">{message}</p>
      <LoadingSpinner size="md" />
    </div>
  );
};

export const LoadingSkeleton = ({ type = 'text', count = 1 }) => {
  const skeletons = {
    text: (
      <div className="space-y-2">
        {[...Array(count)].map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${Math.random() * 30 + 70}%` }} />
        ))}
      </div>
    ),
    card: (
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="h-6 bg-gray-200 rounded animate-pulse w-3/4" />
        <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
        <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
        <div className="h-10 bg-gray-200 rounded animate-pulse w-1/3 mt-4" />
      </div>
    ),
    table: (
      <div className="space-y-2">
        {[...Array(count)].map((_, i) => (
          <div key={i} className="flex gap-4">
            <div className="h-10 bg-gray-200 rounded animate-pulse w-1/4" />
            <div className="h-10 bg-gray-200 rounded animate-pulse w-1/2" />
            <div className="h-10 bg-gray-200 rounded animate-pulse w-1/4" />
          </div>
        ))}
      </div>
    ),
    image: (
      <div className="bg-gray-200 rounded-lg animate-pulse" style={{ paddingBottom: '75%' }} />
    )
  };

  return skeletons[type] || skeletons.text;
};

export const LoadingButton = ({ loading, children, onClick, className = '', disabled = false, ...props }) => {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={`relative ${className} ${loading || disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      {...props}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="sm" color="gray" />
        </span>
      )}
      <span className={loading ? 'invisible' : ''}>{children}</span>
    </button>
  );
};

export const InlineLoader = ({ text = 'Processing...', color = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600'
  };

  return (
    <div className={`flex items-center gap-2 ${colorClasses[color]}`}>
      <LoadingSpinner size="sm" color={color} />
      <span className="text-sm font-medium">{text}</span>
    </div>
  );
};

export const ProgressBar = ({ progress = 0, label = '', showPercentage = true }) => {
  return (
    <div className="w-full">
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-2">
          {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
          {showPercentage && <span className="text-sm text-gray-500">{Math.round(progress)}%</span>}
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
        <div 
          className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
    </div>
  );
};

export const PageLoader = ({ message = 'Loading PricePAL Compare...', tip = '' }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="text-center space-y-6">
        <div className="flex justify-center">
          <div className="relative">
            <LoadingSpinner size="xl" color="purple" />
            <div className="absolute inset-0 flex items-center justify-center text-3xl animate-pulse">
              🛒
            </div>
          </div>
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{message}</h2>
          {tip && <p className="text-gray-600 text-sm max-w-md">{tip}</p>}
        </div>
      </div>
    </div>
  );
};

export default {
  LoadingSpinner,
  LoadingOverlay,
  LoadingCard,
  LoadingSkeleton,
  LoadingButton,
  InlineLoader,
  ProgressBar,
  PageLoader
};
